import { type Character, type CharacterRoster } from '../story/characters';
import { chronicleTally, type ChronicleChapter } from '../story/chronicle';
import { portraitUrl } from '../story/storyLoader';
import { escapeHtml } from './puzzleScreen';

export interface ChronicleView {
  readonly chapters: readonly ChronicleChapter[];
}

export function chronicleMarkup(view: ChronicleView): string {
  const tally = chronicleTally(view.chapters);
  return `
    <div class="screen screen--brief chronicle">
      <header class="sub-header">
        <button class="btn btn--ghost btn--icon" data-action="back" aria-label="Back to the map">←</button>
        <div>
          <h1>Story Codex</h1>
          <p>${tally.seen} of ${tally.total} scenes witnessed</p>
        </div>
      </header>
      <section class="page chronicle__page" aria-labelledby="chronicle-heading">
        <h2 class="page__heading" id="chronicle-heading">Chronicle</h2>
        ${view.chapters.map(chapterMarkup).join('')}
      </section>
      <section class="page chronicle__page" aria-labelledby="cast-heading">
        <h2 class="page__heading" id="cast-heading">Cast</h2>
        <p class="page__empty" data-role="cast">Opening the personnel files…</p>
      </section>
    </div>
  `;
}

function chapterMarkup(chapter: ChronicleChapter): string {
  return `
    <h3 class="chapter">${escapeHtml(chapter.title)}</h3>
    ${chapter.note ? `<p class="chapter__note">${escapeHtml(chapter.note)}</p>` : ''}
    <ul class="page__list chapter__scenes">
      ${chapter.scenes
        .map((scene) =>
          scene.seen
            ? `
              <li class="scene is-seen">
                <button class="scene__replay" data-cutscene="${escapeHtml(scene.id)}">
                  <span class="scene__title">${escapeHtml(scene.title)}</span>
                  <i class="scene__meta">Replay</i>
                </button>
              </li>`
            : `
              <li class="scene">
                <span class="scene__title">???</span>
                <i class="scene__meta">${escapeHtml(scene.hint)}</i>
              </li>`,
        )
        .join('')}
    </ul>
  `;
}

export function castMarkup(roster: CharacterRoster, spoken: ReadonlySet<string>): string {
  if (roster.all.length === 0) return '<p class="page__empty">The personnel files are missing.</p>';
  return `
    <ul class="cast">
      ${roster.all.map((character) => castCardMarkup(character, spoken.has(character.id))).join('')}
    </ul>
  `;
}

function castCardMarkup(character: Character, unlocked: boolean): string {
  const portrait = `<img class="cast__portrait" src="${portraitUrl(character.id)}" alt="" width="72" height="72" />`;
  if (!unlocked) {
    return `
      <li class="cast__card is-locked">
        ${portrait}
        <div class="cast__body">
          <b class="cast__name">???</b>
          <i class="cast__role">Unknown operative</i>
          <p class="cast__bio">Appears in a scene you have not witnessed yet.</p>
        </div>
      </li>
    `;
  }
  return `
    <li class="cast__card" style="--speaker-accent: ${character.accentColor}">
      ${portrait}
      <div class="cast__body">
        <b class="cast__name">${escapeHtml(character.name)}</b>
        <i class="cast__role">${escapeHtml(character.role)}</i>
        <p class="cast__bio">${escapeHtml(character.bio)}</p>
      </div>
    </li>
  `;
}
